// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import { TimeGroup, UpdateTimeGroupRequestSchema } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type UpdateTimeGroupRpc = (request: Init<typeof UpdateTimeGroupRequestSchema>) => Observable<TimeGroup>

export const updateTimeGroup = (updateTimeGroupRpc: UpdateTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimeGroup.match),
      mergeMap(action => updateTimeGroupRpc(action.payload)
        .pipe(
          map(TimelineSlice.upsertTimeGroup),
          catchError(e => handleError(e, action.payload.id ?? "")),
        )
      ),
    )

const handleError = (e: unknown, timeGroupId: string): Observable<Action> => {
  const actions: Action[] = [
    TimelineSlice.setFailedTimeGroupId(timeGroupId),
  ]
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_not_found",
          message: "Failed to update timesheet. Could not find the target timesheet."
        }),
        messageType: "ERROR",
      }))
      actions.push(Actions.onTimelineOutOfSync())
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_permission_denied",
          message: "Failed to update timesheet. You do not have permission to update the timesheet."
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(showSnackBar({
        message: t({
          id: "msg_update_time_group_failed_failed_precondition",
          message: "Failed to update timesheet. The timesheet is already posted and can't be changed anymore."
        }),
        messageType: "ERROR",
      }))
      actions.push(Actions.onTimelineOutOfSync())
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_update_time_group_failed",
      message: "Failed to update the timesheet. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const updateTimeGroupRpc = (request: Init<typeof UpdateTimeGroupRequestSchema>): Observable<TimeGroup> =>
  executorMutation(() => timelineService.updateTimeGroup(create(UpdateTimeGroupRequestSchema, request)))

export default updateTimeGroup(updateTimeGroupRpc)
